<template>
  <div>
    <top-nav title="Household Carbon Footprint Calculator " />
    <div class="guide">
      <div class="title-area">
        Welcome to the Household Carbon Footprint Calculator!
      </div>
      <div class="meta-name row">
        <div class="col s12">
          <p>
            Carbon dioxide is a greenhouse gas that traps heat within the
            Earth’s atmosphere. The significant increase in the concentration of
            carbon dioxide in our atmosphere from the burning of fossil fuels
            since the Industrial Revolution is the primary cause of climate
            change. Cutting global carbon emissions is, therefore, critical to
            limiting climate change and its effects. Everyone has a part to
            play!
          </p>
          <p>
            This simple Household Carbon Footprint Calculator, developed by the
            Organization of Eastern Caribbean States (OECS) Commission, is
            customized for Saint Lucia and converts your household’s total
            annual energy consumption (based on your LUCELEC bill) and your
            cooking gas consumption into tonnes of carbon dioxide released by
            your household into the atmosphere each year.
          </p>
          <p>
            Once you have calculated your household’s carbon footprint, see how
            it compares to average households in other countries and think about
            how your household can reduce its carbon footprint. Check the
            Resource Centre for ideas!
          </p>
        </div>
      </div>
      <collapsibleComponent :is-open="false" :check-default="true" :index="1" body-classes="">
        <template #header>
          <div class="section-title">
            Instructions (Click to view.)
          </div>
        </template>
        <template #body>
          <div class="description">
            <ol>
              <li>
                Get your most recent LUCELEC bill. Look for the kilowatt-hour
                (kWh) energy consumption on your bill. The bill indicates your
                current monthly total and also provides your history of
                consumption over the previous 12 months. View this
                <a href="#" helper-id="F999">sample LUCELEC bill </a> to see
                where to find the required information. Add up your consumption
                for the current month and the previous 11 months to get the
                total for a year (12 months) and enter the information in the
                calculator under Annual Total (kWh).
              </li>
              <li>
                Calculate the total cooking gas you use each year and enter the
                information in the calculator under Annual Total (litres). You
                can do this easily by estimating the number of cooking gas tanks
                you use each year and multiplying that by the size of tank used
                (in liters). The standard size tanks available in Saint Lucia
                are:
                <ul style="padding: 12px 0 0 0;">
                  <li>- 20 lbs (≈ 18 litres) – small metal tank</li>
                  <li>- 22 lbs (≈ 20 litres) – composite bottle</li>
                  <li>- 25lbs (≈ 22 litres) – small metal tank</li>
                  <li>- 50lbs (≈ 45 litres) - metal tank</li>
                  <li>- 100lbs (≈ 90 litres) – large metal tank</li>
                </ul>
              </li>
              <!-- <li>The basic data required can be found on your utility bills (covering a 12 month period).</li> -->
              <!-- <li>This template can also be printed and filled in manually. Your carbon footprint is simple enough to calculate with a pocket calculator. </li> -->
            </ol>
          </div>

          <div class="card z-depth-0 grey lighten-4">
            <div class="card-content">
              <b>Note:</b> ‘Factor’ represents the number of kilograms of carbon
              dioxide (CO<sub>2</sub>) generated by one (1) unit of the
              indicated energy source.
            </div>
          </div>
        </template>
      </collapsibleComponent>

      <div class="card">
        <div class="card-content">
          <div class="row">
            <div class="col s12">
              <div class="section-title">Electricity (from LUCELEC)</div>
            </div>

            <div class="input-field col s7 m4 ">
              <div class="elec_annual_total">Annual Total (kWh)</div>
              <input @input="$_saveScore()" id="elec_annual_total" type="number" :disabled="userRole === 'subscriber'"
                min="0" placeholder="0" v-model="electricity.annual_total" />
            </div>
            <div class="input-field col s2">
              <div for="elec_factor">Factor</div>
              <div class="calc-value">
                0.65
              </div>
            </div>
            <div class="input-field col s12 m6">
              <!--    <input
              @input="$_saveScore()"
              id="elec_total"
              type="number"
              min="0"
              :value=""
              disabled
            /> -->
              <div class="custom_label">
                Kilograms (Kg) of carbon dioxide (CO<sub>2</sub>) released
              </div>
              <div class="calc-value">
                {{ parseFloat(electricity_total).toFixed(1) }} Kg
              </div>
            </div>
          </div>
          <div class="row">
            <div class="section-title col s12">Gas (LPG, propane/butane)</div>
            <div class="input-field col s7 m4">
              <div class="custom-label">Annual Total (litres)</div>
              <input @input="$_saveScore()" id="gas_annual_total" type="number" min="0"
                :disabled="userRole === 'subscriber'" placeholder="0" v-model="gas.annual_total" />
            </div>
            <div class="input-field col s2">
              <div class="gas_factor">Factor</div>
              <div class="calc-value">{{ gas.factor }}</div>
            </div>
            <div class="input-field col s12 m6">
              <!--   <input id="gas_total" type="number"  min="0" :value="gas_total" disabled /> -->
              <div class="gas_total">
                Kilograms (Kg) of carbon dioxide (CO<sub>2</sub>) released
              </div>
              <div class="calc-value">
                {{ parseFloat(gas_total).toFixed(1) }} Kg
              </div>
            </div>
          </div>

          <div class="col s12">
            <p>
              TOTAL Carbon Footprint (carbon
              dioxide (CO<sub>2</sub>) released per year by
              household) =
              <span style="font-weight:bold">{{
                (c_total / 1000).toFixed(2)
              }}</span>
              Tonnes
            </p>
          </div>
          <div class="end-of-profile">
            <router-link :to="{ name: 'Home' }" class="btn-large btn-rounded waves-effect indigo accent-2">
              Finish
            </router-link>
          </div>
        </div>
      </div>
    </div>
    <app-bottom-tab />
  </div>
</template>
<script>
import topNav from "@/components/topNav";
import collapsibleComponent from "@/components/collapsible.component";
import AppBottomTab from "@/components/AppBottomTab";
import { mapState, mapActions } from "vuex";
import { updateUserProfile } from "@/api/user.api";
import M from "materialize-css";
export default {
  name: "EnergyCalculator",
  components: {
    topNav,
    collapsibleComponent,
    AppBottomTab,
  },
  data() {
    return {
      electricity: {
        annual_total: "",
        factor: 0.6526,
      },
      gas: {
        annual_total: "",
        factor: 1.5,
      },
    };
  },
  mounted() {
    this.updateTextFields();
    this.$_setDefaultValues();
    this.$_setupHelpers();
  },
  beforeDestroy() {
    this.$_saveData();
  },
  computed: {
    ...mapState({
      energyX: (state) => state.User.user.energy,
      user: (state) => state.User,
    }),
    userRole: {
      get() {
        return "user"
        return this.user.user && this.user.user.roles.length > 0
          ? this.user.user.roles[0]
          : false
      }
    },
    energy: {
      get() {
        return this.energyX ? JSON.parse(this.energyX) : false;
      },
    },
    electricity_total() {
      if (this.electricity.annual_total) {
        return (
          parseFloat(this.electricity.annual_total) *
          parseFloat(this.electricity.factor)
        );
      }
      return 0;
    },
    gas_total() {
      if (this.gas.annual_total) {
        return parseFloat(this.gas.annual_total) * parseFloat(this.gas.factor);
      }
      return 0;
    },
    c_total() {
      return (
        parseFloat(this.electricity_total) + parseFloat(this.gas_total)
      ).toFixed(2);
    },
  },
  methods: {
    ...mapActions("User", ["updateUser"]),
    $_setDefaultValues() {
      if (this.energy) {
        this.electricity = this.energy.electricity;
        this.gas = this.energy.gas;
      }
    },

    $_setupHelpers() {
      const helpers = this.$el.querySelectorAll("[helper-id]");

      if (helpers) {
        helpers.forEach((helperEl) => {
          helperEl.addEventListener("click", (event) => {
            event.preventDefault();
            const helperId = helperEl.getAttribute("helper-id");
            const styles = helperEl.getAttribute("helper-style");
            this.$store.dispatch("Helpers/setHelper_", {
              id: helperId,
              styles: styles ? styles : "",
            });
            /*  this.$store.dispatch("Helpers/setHelper", helperId); */
          });
        });
      }
    },
    $_saveScore() {
      this.$store.commit("User/UPDATE_ENERGY", {
        electricity: this.electricity,
        gas: this.gas,
        /*  c_total: this.c_total,
        electricity_total: this.electricity_total,
        gas_total: this.gas_total */
      });
    },
    $_saveData() {
      return
      const payload = {
        fields: {
          energy: JSON.stringify(this.energy),
        },
      };
      updateUserProfile(this.user.user.profile, payload)
        .then(() => {
          //  console.log(res);
          //toastMessage('Profile updated successfully')
        })
        .catch((err) => {
          this.isProcessing = false;
        });
    },
    updateTextFields() {
      setTimeout(() => {
        M.updateTextFields();
      }, 300);
    },
  },
};
</script>
<style scoped lang="scss">
.guide {
  max-width: 768px;
  margin: auto;
  padding: 0 16px 68px;
  margin-top: 48px;

  p,
  li {
    text-align: justify;
  }

  .section-title {
    font-size: 20px;
  }

  ol {
    li {
      margin-bottom: 6px;
    }
  }

  .title-area {
    font-size: 26px;
    margin: 12px 8px 2px;
    position: relative;
  }

  .input-field.col label {
    font-size: 16px;
    left: 1rem;
  }
}

.calc-value {
  font-size: 19px;
  font-weight: 600;
}

.end-of-profile {
  margin-bottom: -6px;
  text-align: center;
  padding: 24px;

  a {
    width: 200px;
  }
}
</style>
